import { platformApi } from '@/apis';
import { alertsAtom } from '@/atoms';
import ir3Logo from '@/svg/ir3Logo.svg';
import { notify, reviveDate } from '@/utils';
import { eventNotifications } from '@/utils/config';
import { captureMessage } from '@sentry/react';
import { useAtom } from 'jotai';
import { parseServerSentEvents } from 'parse-sse';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';

export function useAccelerometerAlertStream() {
  const [, setAlerts] = useAtom(alertsAtom);
  const navigate = useNavigate();

  useEffect(() => {
    if (!eventNotifications.accelerometerAlerts) {
      return;
    }

    const ctrl = new AbortController();

    async function initiateStream() {
      try {
        const response = await platformApi.get('sse/accelerometer-alerts', {
          signal: ctrl.signal,
          headers: { accept: 'text/event-stream' },
        });

        for await (const msg of parseServerSentEvents(response)) {
          captureMessage('Received accelerometer alert', {
            level: 'debug',
            extra: { msg },
          });

          if (msg.event === 'insert') {
            const alert = JSON.parse(msg.data, reviveDate);

            notify(
              'Accelerometer Alert',
              {
                tag: alert.identifier,
                body: `${
                  alert.driver
                    ? `${alert.driver.forenames} ${alert.driver.surname} (${alert.driver.collarNumber})`
                    : 'Unknown driver'
                } - ${alert.vehicle.registrationNumber} (${
                  alert.vehicle.fleetNumber
                })`,
                icon: ir3Logo,
                timestamp: Math.floor(alert.time),
              },
              () => {
                navigate(`/live/events/${alert.identifier}`);
              },
            );

            setAlerts((alerts) => alerts.concat(alert));
          }

          if (msg.event === 'update') {
            const alert = JSON.parse(msg.data, reviveDate);

            if (alert.claimedBy) {
              setAlerts((alerts) =>
                alerts.filter((a) => a.identifier !== alert.identifier),
              );
            }
          }
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          captureMessage('Stream aborted', { level: 'debug' });
        } else {
          throw error;
        }
      }
    }

    initiateStream();

    return () => {
      ctrl.abort();
    };
  }, [navigate, setAlerts]);
}
